import React from 'react';
import HowItWorksPage from '../components/HowItWorksPage';
import { StatusCards, Comparison, InfoCallout, UiLabel } from '../components/HelpArticleLayout';

// Compliance · How it all works — the staff-side explainer for the day-to-day
// Compliance page (Review Queue + Compliance Overview).
//
// Distinct from /documents/how-it-works, which explains the CONFIGURATION —
// the library, the categories, the risk grid. This one is about the work:
// what to approve, what the colours mean, and which tab does which job.

const ICON = {
  map:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>,
  inbox:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>,
  gauge:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 12l4-2.5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" /></svg>,
  split:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v16" /><path d="M6 9L3 12l3 3" /><path d="M18 9l3 3-3 3" /></svg>,
  clock:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  bolt:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>,
  send:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>,
};

const sections = [
  {
    id: 'picture', title: 'Two tabs, two jobs', icon: ICON.map,
    body: (
      <>
        <p>The Compliance page is split down one line, and it is worth holding onto:</p>
        <Comparison
          left={{ heading: 'Review Queue', body: 'For DOING. Every per-document action lives here — approve, view the file, ask for clarification, reject. If you are working through submissions, you are on this tab.' }}
          right={{ heading: 'Compliance Overview', body: 'For KNOWING. The state of the estate and where the risk is — who is red and why, what lapses next, who to chase. It never approves anything; it links you into the queue.' }}
        />
        <p>Setting up <em>which</em> documents exist and who owes them is a third thing again, and lives under <UiLabel>Compliance Configuration</UiLabel>.</p>
      </>
    ),
  },
  {
    id: 'queue', title: 'The Review Queue, grouped by whose move it is', icon: ICON.inbox,
    body: (
      <>
        <p>The pills are not a status list — they are grouped by who has to act:</p>
        <ul className="list-disc pl-5 space-y-1.5 my-1">
          <li><strong className="text-ink">Needs you</strong> — Awaiting review, Expired, Never submitted.</li>
          <li><strong className="text-ink">With the supplier</strong> — Needs clarification, Rejected. They owe a reply or a replacement.</li>
          <li><strong className="text-ink">Watch</strong> — Expiring soon. Nothing to do today.</li>
          <li><strong className="text-ink">History</strong> — everything already decided.</li>
        </ul>
        <p><strong className="text-ink">Never submitted</strong> is: published to the supplier, required of them, and nothing sent back after a seven-day grace period. It counts what <em>they</em> owe, which is why it is not called &ldquo;never sent&rdquo; — that read as though <em>we</em> had failed to send it, and until recently that was sometimes true as well.</p>
        <InfoCallout>A document that has never been published to a supplier does <strong>not</strong> appear here, because that gap is ours rather than theirs. Publishing happens automatically when a risk tier is set, and repairs itself when a supplier&rsquo;s record is opened — so if you find a required document sitting unpublished, open the supplier and it will go out.</InfoCallout>
        <InfoCallout>Asking for clarification is not the same as rejecting. Clarification opens a thread and keeps the document alive; rejection sends them back to the start. Use the first unless the document is genuinely wrong.</InfoCallout>
      </>
    ),
  },
  {
    id: 'colours', title: 'Where a supplier’s colour comes from', icon: ICON.gauge,
    body: (
      <>
        <p>Green, amber and red are computed in the database, per supplier, from the documents they owe and the state each one is in. Nothing on these screens sets a colour by hand.</p>
        <StatusCards items={[
          { color: '#009159', label: 'Green', description: 'Everything required is approved and in date.' },
          { color: '#E89A2E', textColor: '#8A5A12', label: 'Amber', description: 'Something expires within 30 days, is awaiting your review, or the supplier owes a reply.' },
          { color: '#E0290A', textColor: '#B02309', label: 'Red', description: 'Something required is expired, was rejected, or has never been supplied. These suppliers cannot be awarded work.' },
        ]} />
        <p>Which documents a supplier owes is their <strong className="text-ink">risk tier</strong>, plus any per-supplier additions, minus anything waived — and only counting documents that are actually published. A requirement pointing at an archived document is ignored rather than chased.</p>
      </>
    ),
  },
  {
    id: 'split', title: 'Expired and never supplied are not the same problem', icon: ICON.split,
    body: (
      <>
        <p>The database returns the same <strong className="text-ink">red</strong> for both, which is correct — both block an award. But they want opposite responses, so the Overview splits them out:</p>
        <Comparison
          left={{ heading: 'Expired', body: 'They gave it to us once and it has lapsed. Chase a renewal from a supplier who is already set up and engaged.' }}
          right={{ heading: 'Never supplied', body: 'They have never sent it at all. Usually a supplier who was never properly switched on — often the real fix is a portal account, not a reminder.' }}
        />
        <InfoCallout tone="info">If most of the red is &ldquo;never supplied&rdquo;, the number is an onboarding backlog wearing a compliance-failure badge. That is a different conversation from an estate whose cover is lapsing.</InfoCallout>
      </>
    ),
  },
  {
    id: 'forward', title: 'Looking forward, and chasing', icon: ICON.clock,
    body: (
      <>
        <p>The queue only shows what suppliers have already sent. The Overview looks the other way — documents currently valid that will lapse in <strong className="text-ink">7, 30, 60 or 90 days</strong>.</p>
        <p>Only the first two windows are amber; 60 and 90 days out a supplier is still green. That is the point of the section: it is how today&rsquo;s amber becomes next month&rsquo;s red, caught early.</p>
        <p>Chasing from the Overview is deliberately <strong className="text-ink">batched</strong> — remind every amber supplier, or chase everyone missing one particular document. Every send confirms first, naming exactly who will be contacted and what is being asked for, and lands in the audit trail like any other action.</p>
      </>
    ),
  },
  {
    id: 'auto', title: 'Auto-approval, and when it holds back', icon: ICON.bolt,
    body: (
      <>
        <p>A document can be set to approve submissions automatically, with a spot-check percentage. Three things override it and send the submission to a person anyway:</p>
        <ul className="list-disc pl-5 space-y-1.5 my-1">
          <li>The supplier <strong className="text-ink">entered an expiry date</strong> — a date somebody should eyeball.</li>
          <li>The supplier <strong className="text-ink">edited their identity details</strong> on the way through.</li>
          <li>It was <strong className="text-ink">drawn as a spot check</strong>.</li>
        </ul>
        <p>The decision is made in the database, not the browser, so a supplier can never approve their own document by driving the page differently.</p>
      </>
    ),
  },
  {
    id: 'reminders', title: 'What goes out automatically', icon: ICON.send,
    body: (
      <>
        <p>Suppliers are reminded about expiring and expired documents on a schedule, and told when a document is published to them, approved, rejected, or needs re-signing after a material change.</p>
        <p>Internally, the daily round-up carries the standing counts — documents awaiting review, expired documents and the suppliers behind them, and suppliers with no portal account. It is opt-in per person and stays silent on a day with nothing to report, deliberately: a digest that arrives every morning saying &ldquo;all clear&rdquo; stops being opened.</p>
      </>
    ),
  },
];

export default function ComplianceInfo() {
  return (
    <HowItWorksPage
      sections={sections}
      intro="The compliance work end to end — which tab does which job, what the pills and colours actually mean, why expired and never-supplied need different responses, and what the system chases on your behalf."
      backTo="/compliance"
      backLabel="Compliance"
      adminOnly
      restrictedNote="Compliance is restricted to Admin and Super User accounts."
    />
  );
}
